// Jax - Counter Strike: spins for 1 turn dodging every incoming auto-attack,
// then slams the closest 2 enemies dealing physical damage scaled by autos blocked.
// Blue buff double cast: stance is already active, so skip re-applying it and
// deal the slam damage immediately instead.

import { physicalDamage } from "../../combat/DamageCalc";
import { effectiveStats } from "../UnitStats";
import type { Ability, AbilityContext, AbilityHit } from "./Ability";

const STANCE_DURATION = 1;
const SLAM_AD_RATIO = 0.65;
const SLAM_FLAT = 55;
const SLAM_BLOCK_BONUS = 0.20;

export const jaxCounterStrike: Ability = {
  id: "jaxCounterStrike",
  displayName: "Counter Strike",
  description: "Spins for 1 turn dodging all auto-attacks, then slams the closest 2 enemies for physical damage (more per auto blocked).",
  iconId: "JaxE",

  targetShape({ caster, combat }: AbilityContext) {
    if (caster.jaxCounterStrikeTurnsRemaining > 0) {
      const enemyTeam = caster.team === "player" ? "enemy" : "player";
      return combat.livingUnitsOf(enemyTeam)
        .sort((a, b) => a.pos.q - b.pos.q)
        .slice(0, 2)
        .map(u => u.pos);
    }
    return [caster.pos];
  },

  computeHits({ caster, combat }: AbilityContext): AbilityHit[] {
    // Already in stance (blue buff double cast) — deal slam damage immediately.
    if (caster.jaxCounterStrikeTurnsRemaining > 0) {
      const stats = effectiveStats(caster);
      const blocked = caster.jaxCounterStrikeBlockedCount;
      const slamDmg = physicalDamage((stats.ad * SLAM_AD_RATIO + SLAM_FLAT) * (1 + SLAM_BLOCK_BONUS * blocked));
      const enemyTeam = caster.team === "player" ? "enemy" : "player";
      const targets = combat.livingUnitsOf(enemyTeam)
        .sort((a, b) => a.pos.q - b.pos.q)
        .slice(0, 2);
      return targets.map(target => ({
        target,
        amount: slamDmg,
        type: "physical" as const,
      }));
    }
    return [{
      target: caster,
      amount: 0,
      type: "none",
      effect: { kind: "jaxCounterStrike", duration: STANCE_DURATION },
    }];
  },
};
